import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, Phone } from "lucide-react";
import { toast } from "sonner";
import { BUSINESS, SERVICE_OPTIONS, SIZE_OPTIONS } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-none border-0 border-b-2 border-black bg-transparent py-3 px-1 text-base placeholder:text-black/40 focus:outline-none focus:border-[#FF4E00] transition-colors min-h-[44px]";

export const BookingForm = () => {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", service: SERVICE_OPTIONS[0],
    dumpster_size: SIZE_OPTIONS[0], address: "", preferred_date: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address) {
      toast.error("Please fill in your name, phone and address.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email) delete payload.email;
      await axios.post(`${API}/bookings`, payload);
      setDone(true);
      toast.success("Request sent! Moe will call you back shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="book" className="border-b border-black bg-[#FF4E00]" data-testid="booking-section">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 lg:grid-cols-2">
        <div className="px-4 sm:px-8 py-20 lg:py-28 text-white border-b border-black/20 lg:border-b-0 lg:border-r">
          <div className="text-xs font-bold uppercase tracking-[0.25em]">Free Quote</div>
          <h2 className="mt-3 font-anton text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.9]">
            Request a<br />pickup
          </h2>
          <p className="mt-6 max-w-sm text-white/90 text-lg">
            Tell us what you're clearing out. We'll text or call back fast with an honest quote — usually same day.
          </p>
          <a
            href={`tel:${BUSINESS.phoneRaw}`}
            data-testid="booking-call-link"
            className="mt-8 inline-flex items-center gap-2 border-2 border-white px-6 py-4 font-anton text-xl uppercase transition-colors hover:bg-white hover:text-[#FF4E00]"
          >
            <Phone size={18} /> Or call {BUSINESS.phone}
          </a>
        </div>

        <div className="bg-white px-4 sm:px-8 py-20 lg:py-28">
          {done ? (
            <motion.div
              data-testid="booking-success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-start gap-4"
            >
              <CheckCircle2 size={56} className="text-[#FF4E00]" />
              <h3 className="font-anton text-4xl uppercase tracking-tight">Request received!</h3>
              <p className="text-muted-foreground">Thanks, {form.name.split(" ")[0]}. Moe's team will reach out to <b>{form.phone}</b> shortly to confirm your {form.service.toLowerCase()}.</p>
              <button
                data-testid="booking-reset-btn"
                onClick={() => { setDone(false); setForm({ ...form, message: "" }); }}
                className="mt-2 border-2 border-black px-6 py-3 font-bold uppercase tracking-wide transition-colors hover:bg-black hover:text-white"
              >
                Send another
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="booking-form">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <input data-testid="booking-name" className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} />
                <input data-testid="booking-phone" className={inputCls} placeholder="Phone number *" value={form.phone} onChange={set("phone")} />
              </div>
              <input data-testid="booking-email" type="email" className={inputCls} placeholder="Email (optional)" value={form.email} onChange={set("email")} />
              <input data-testid="booking-address" className={inputCls} placeholder="Service address *" value={form.address} onChange={set("address")} />

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.15em] text-black/60">Service</label>
                  <select data-testid="booking-service" className={inputCls} value={form.service} onChange={set("service")}>
                    {SERVICE_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.15em] text-black/60">Dumpster size</label>
                  <select data-testid="booking-size" className={inputCls} value={form.dumpster_size} onChange={set("dumpster_size")}>
                    {SIZE_OPTIONS.map((s) => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-[0.15em] text-black/60">Preferred date</label>
                <input data-testid="booking-date" type="date" className={inputCls} value={form.preferred_date} onChange={set("preferred_date")} />
              </div>

              <textarea data-testid="booking-message" rows={3} className={inputCls} placeholder="What are you clearing out?" value={form.message} onChange={set("message")} />

              <button
                data-testid="booking-submit"
                type="submit"
                disabled={loading}
                className="hard-shadow-orange flex w-full items-center justify-center gap-2 bg-black px-8 py-5 font-anton text-xl uppercase tracking-wide text-white transition-transform hover:translate-x-1 hover:translate-y-1 hover:shadow-none disabled:opacity-60"
              >
                {loading ? <><Loader2 className="animate-spin" size={20} /> Sending…</> : "Request A Quote"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
